import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import { User, Child } from '../types';
import { SESSION_KEY_LOCATION_ID, SESSION_KEY_USER_DATA } from '../constants';
import registerChildren from '../hooks/user/registerChildren';

interface UserContextType {
    user: User | null;
    saveUser: (userData: User) => Promise<boolean>;
    addChildToUser: (childData: Child) => void;
    handleDeleteChild: (childId: number) => void;
    updateUserAndSession: (userData: User) => void;
    resetUserContext: () => void;
    selectedChild: Child | null;
    setSelectedChild: (child: Child | null) => void;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

interface UserProviderProps {
    children: ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [selectedChild, setSelectedChildState] = useState<Child | null>(null);

    useEffect(() => {
        const storedUserData = localStorage.getItem(SESSION_KEY_USER_DATA);
        if (storedUserData) {
            setUser(JSON.parse(storedUserData));
        }
    }, []);

    useEffect(() => {
        if (user && user.children && user.children.length > 0 && !selectedChild) {
            setSelectedChildState(user.children[0]);
        }
    }, [user, selectedChild]);

    const updateUserAndSession = (updatedUser: User) => {
        let mappedUser = {
            ...updatedUser,
            children: updatedUser.children.map(child => ({ ...child, isNew: false })),
        };
        setUser(mappedUser);
        localStorage.setItem(SESSION_KEY_USER_DATA, JSON.stringify(updatedUser));
    };

    const resetUserContext = () => {
        setUser(null);
    };

    const saveUser = async (userData: User): Promise<boolean> => {
        const newChildren = userData.children.filter((child) => child.isNew === true);
        const oldChildren = userData.children.filter((child) => child.isNew !== true);
        const location = localStorage.getItem(SESSION_KEY_LOCATION_ID);
        const newChildrenData = newChildren.map((child) => ({
            firstname: child.fname,
            lastname: child.lname,
            dob: new Date(child.dob).toLocaleDateString('en-GB'),
            gender: child.gender,
            location
        }));

        try {
            let newChildIds: number[] | null = [];

            if (newChildrenData.length > 0) {
                const registerChildrenResponse = await registerChildren(userData.uid, newChildrenData);

                if (registerChildrenResponse.success) {
                    newChildIds = registerChildrenResponse.data;
                } else {
                    return false;
                }
            }

            const updatedUser = {
                ...userData,
                children: [
                    ...oldChildren,
                    ...newChildren
                        .filter((_, index) => newChildIds && index < newChildIds.length)
                        .map((child, index) => ({
                            ...child,
                            id: newChildIds ? newChildIds[index] : child.id,
                            isNew: false
                        }))
                ],
            };

            updateUserAndSession(updatedUser);
            return true;
        } catch (error) {
            return false;
        }
    };

    const addChildToUser = (childData: Child) => {
        setUser((currentUser) => {
            if (currentUser) {
                const updatedUser = {
                    ...currentUser,
                    children: [...currentUser.children, childData],
                };
                return updatedUser;
            }
            return currentUser;
        });
    };

    const handleDeleteChild = (childId: number) => {
        setUser((currentUser) => {
            if (currentUser) {
                const updatedUser = {
                    ...currentUser,
                    children: currentUser.children.filter((child) => child.id !== childId),
                };
                return updatedUser;
            }
            return currentUser;
        });
    };

    const updateSelectedChild = (child: Child | null) => {
        setSelectedChildState(child);
        if (user && child) {
            setUser(prevUser => ({
                ...prevUser!,
                selectedChild: child
            }));
        }
    };

    return (
        <UserContext.Provider value={{
            user,
            saveUser,
            addChildToUser,
            handleDeleteChild,
            updateUserAndSession,
            resetUserContext,
            selectedChild,
            setSelectedChild: updateSelectedChild
        }}>
            {children}
        </UserContext.Provider>
    );
};

export const useUser = () => {
    const context = useContext(UserContext);
    if (context === undefined) {
        throw new Error('useUser must be used within a UserProvider');
    }
    return context;
};
